/**
 * Command Palette Service - Nuvra product command index.
 * Navigation, actions, macros, history, smart suggestions.
 *
 * Only real, wired routes are referenced here.
 * No aspirational or speculative destinations.
 */

export type CommandCategory = "navigation" | "action" | "ai-build" | "recent" | "favorite" | "suggested" | "macro";

export interface PaletteCommand {
  id:            string;
  label:         string;
  category:      CommandCategory;
  icon:          string;
  keywords:      string;
  route?:        string;
  description?:  string;
  shortcut?:     string;
  contextRoutes?: string[];
  destructive?:  boolean;
}

export const navigationCommands: PaletteCommand[] = [
  { id: "nav-dashboard",     label: "Dashboard",        category: "navigation", icon: "LayoutDashboard", keywords: "home overview command center", route: "/dashboard", shortcut: "⌘⇧D" },
  { id: "nav-ai-builder",    label: "Build",            category: "navigation", icon: "Bot",             keywords: "generate create ai build new app assistant", route: "/dashboard"  },
  { id: "nav-deploy",        label: "Deploy",           category: "navigation", icon: "Rocket",          keywords: "publish launch ship production release", route: "/deploy" },
  { id: "nav-mobile",        label: "Mobile Builds",    category: "navigation", icon: "Smartphone",      keywords: "android ios apk ipa mobile app", route: "/mobile" },
  { id: "nav-marketplace",   label: "Marketplace",      category: "navigation", icon: "Store",           keywords: "plugins extensions addons agents browse buy templates", route: "/marketplace" },
  { id: "nav-creator",       label: "Creator Studio",   category: "navigation", icon: "TrendingUp",      keywords: "publish sell earn revenue listings creator monetize studio", route: "/creator" },
  { id: "nav-collaboration", label: "Collaboration",    category: "navigation", icon: "Users",           keywords: "team share invite members access", route: "/collaboration" },
  { id: "nav-platform",      label: "Platform",         category: "navigation", icon: "Activity",        keywords: "runtime platform status observability", route: "/platform" },
  { id: "nav-help",          label: "Help Center",      category: "navigation", icon: "LifeBuoy",         keywords: "help support documentation guides", route: "/help" },
  { id: "nav-whats-new",     label: "What's New",       category: "navigation", icon: "Zap",             keywords: "changelog updates release notes new features", route: "/whats-new" },
  { id: "nav-pricing",       label: "Pricing & Plans",  category: "navigation", icon: "CreditCard",      keywords: "upgrade plan pricing acu starter pro studio cost compare", route: "/pricing" },
  { id: "nav-billing",       label: "Billing",          category: "navigation", icon: "CreditCard",      keywords: "subscription plan payment acu credits balance top up", route: "/billing" },
  { id: "nav-settings",      label: "Settings",         category: "navigation", icon: "Settings",        keywords: "preferences account profile api keys security", route: "/settings" },
];

export const actionCommands: PaletteCommand[] = [
  { id: "act-new-project",    label: "Create new project",          category: "action", icon: "Plus",         keywords: "new build start create app", route: "/dashboard",      description: "Start a new AI-powered project" },
  { id: "act-deploy",         label: "Deploy current project",      category: "action", icon: "Rocket",       keywords: "deploy ship publish launch", route: "/deploy",          description: "Deploy your active project" },
  { id: "act-rollback",       label: "Rollback deployment",         category: "action", icon: "RotateCcw",    keywords: "undo revert previous version", route: "/deploy",         description: "Revert to a previous deployment", destructive: true },
  { id: "act-invite",         label: "Invite collaborator",         category: "action", icon: "UserPlus",     keywords: "team share invite member add", route: "/collaboration", description: "Add a team member" },
  { id: "act-marketplace",    label: "Browse marketplace",          category: "action", icon: "Store",        keywords: "plugins extensions install agents find", route: "/marketplace", description: "Find plugins, agents and templates" },
  { id: "act-mobile-build",   label: "Build mobile app",            category: "action", icon: "Smartphone",   keywords: "android ios apk compile mobile", route: "/mobile",       description: "Generate a native mobile build" },
  { id: "act-health",         label: "Show health score",           category: "action", icon: "HeartPulse",   keywords: "project health score radar status", route: "/dashboard",  description: "View project health overview" },
  { id: "act-alerts",         label: "Check alerts",                category: "action", icon: "Bell",         keywords: "incidents notifications warnings", route: "/dashboard",   description: "View active incident alerts" },
  { id: "act-compose-agents", label: "Compose AI agents",           category: "action", icon: "Cpu",          keywords: "compose combine agents super-agent marketplace", route: "/marketplace", description: "Combine agents into a super-agent" },
  { id: "act-upgrade",        label: "Upgrade plan",                category: "action", icon: "Star",         keywords: "upgrade plan pricing acu pro studio unlock premium", route: "/pricing", description: "View and upgrade your subscription" },
  { id: "act-publish-app",    label: "Publish app to marketplace",  category: "action", icon: "TrendingUp",   keywords: "publish sell earn creator revenue listing marketplace", route: "/creator", description: "List your app in the Nuvra Marketplace" },
  { id: "act-top-up",         label: "Top up ACU balance",          category: "action", icon: "Zap",          keywords: "top up acu credits balance buy compute units", route: "/billing", description: "Purchase additional AI Compute Units" },
];

export const allCommands: PaletteCommand[] = [...navigationCommands, ...actionCommands];

// ---- History ----
const HISTORY_KEY = "nuvra-command-history";
const MAX_HISTORY = 50;

export function getCommandHistory(): string[] {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); } catch { return []; }
}

export function addToHistory(commandId: string): void {
  const history = getCommandHistory().filter((id) => id !== commandId);
  history.unshift(commandId);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

export function getRecentCommands(limit = 8): PaletteCommand[] {
  return getCommandHistory().slice(0, limit)
    .map((id) => allCommands.find((c) => c.id === id))
    .filter(Boolean) as PaletteCommand[];
}

// ---- Favorites ----
const FAVORITES_KEY = "nuvra-command-favorites";

export function getFavorites(): string[] {
  try { return JSON.parse(localStorage.getItem(FAVORITES_KEY) || "[]"); } catch { return []; }
}

export function toggleFavorite(commandId: string): boolean {
  const favs = getFavorites();
  const idx = favs.indexOf(commandId);
  if (idx >= 0) { favs.splice(idx, 1); localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs)); return false; }
  favs.push(commandId);
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
  return true;
}

export function isFavorite(commandId: string): boolean {
  return getFavorites().includes(commandId);
}

export function getFavoriteCommands(): PaletteCommand[] {
  return getFavorites().map((id) => allCommands.find((c) => c.id === id)).filter(Boolean) as PaletteCommand[];
}

// ---- Macros ----
export interface CommandMacro {
  id: string;
  name: string;
  trigger: string; // e.g. "/deploy-all"
  steps: string[]; // command ids to execute in sequence
  createdAt: number;
}

const MACROS_KEY = "nuvra-command-macros";

export function getMacros(): CommandMacro[] {
  try { return JSON.parse(localStorage.getItem(MACROS_KEY) || "[]"); } catch { return []; }
}

export function saveMacro(macro: CommandMacro): void {
  const macros = getMacros().filter(m => m.id !== macro.id);
  macros.push(macro);
  localStorage.setItem(MACROS_KEY, JSON.stringify(macros));
}

export function deleteMacro(id: string): void {
  localStorage.setItem(MACROS_KEY, JSON.stringify(getMacros().filter(m => m.id !== id)));
}

export function findMacroByTrigger(trigger: string): CommandMacro | undefined {
  return getMacros().find(m => m.trigger.toLowerCase() === trigger.toLowerCase());
}

// ---- Undo Stack ----
export interface UndoAction {
  id: string;
  label: string;
  commandId: string;
  timestamp: number;
  route?: string;
}

const UNDO_KEY = "nuvra-undo-stack";
const MAX_UNDO = 20;

export function getUndoStack(): UndoAction[] {
  try { return JSON.parse(localStorage.getItem(UNDO_KEY) || "[]"); } catch { return []; }
}

export function pushUndo(action: UndoAction): void {
  const stack = getUndoStack();
  stack.unshift(action);
  localStorage.setItem(UNDO_KEY, JSON.stringify(stack.slice(0, MAX_UNDO)));
}

export function popUndo(): UndoAction | undefined {
  const stack = getUndoStack();
  const action = stack.shift();
  localStorage.setItem(UNDO_KEY, JSON.stringify(stack));
  return action;
}

// ---- Smart Suggestions ----
export function getSuggestedCommands(currentPath: string): PaletteCommand[] {
  const contextMap: Record<string, string[]> = {
    "/dashboard":     ["act-new-project", "act-deploy", "act-health", "act-alerts"],
    "/deploy":        ["act-rollback", "nav-platform", "nav-billing"],
    "/marketplace":   ["act-compose-agents", "act-marketplace", "nav-creator"],
    "/creator":       ["act-publish-app", "nav-marketplace", "act-upgrade"],
    "/billing":       ["act-upgrade", "act-top-up", "nav-pricing"],
    "/pricing":       ["act-upgrade", "act-top-up", "nav-billing"],
    "/settings":      ["nav-billing", "act-invite", "nav-pricing"],
    "/collaboration": ["act-invite", "nav-projects", "act-deploy"],
    "/mobile":        ["act-mobile-build", "act-deploy", "nav-projects"],
    "/platform":      ["act-health", "act-alerts", "nav-dashboard"],
  };
  const ids = contextMap[currentPath] || ["act-new-project", "nav-dashboard", "act-deploy", "act-upgrade"];
  return ids.map(id => allCommands.find(c => c.id === id)).filter(Boolean) as PaletteCommand[];
}

// ---- AI Build Detection ----
export function isAIBuildQuery(query: string): boolean {
  const trimmed = query.trim();
  if (trimmed.length < 12) return false;
  const lowerQuery = trimmed.toLowerCase();
  const matchesCommand = allCommands.some(
    (cmd) => cmd.label.toLowerCase().includes(lowerQuery) || cmd.keywords.toLowerCase().includes(lowerQuery)
  );
  if (matchesCommand) return false;
  return /^(build|create|generate|make|design|develop|set up|add|implement|show me|find|explain|optimize|fix|compose)\s/i.test(trimmed) ||
    trimmed.split(" ").length >= 4;
}

export function detectNLIntent(query: string): { type: "navigate" | "action" | "build" | "macro" | "search"; target?: string } {
  const q = query.toLowerCase().trim();

  // Macro trigger
  if (q.startsWith("/")) {
    const macro = findMacroByTrigger(q);
    if (macro) return { type: "macro", target: macro.id };
  }

  if (/^(go to|open|show|navigate to)\s/i.test(q)) {
    const target = q.replace(/^(go to|open|show|navigate to)\s+/i, "");
    const match = allCommands.find(c => c.label.toLowerCase().includes(target) || c.keywords.includes(target));
    if (match) return { type: "navigate", target: match.route };
  }

  if (/^(run|execute|trigger|start)\s/i.test(q)) return { type: "action" };
  if (isAIBuildQuery(query)) return { type: "build" };
  return { type: "search" };
}
